import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState
} from "react";

declare global {
  interface WindowEventMap {
    "local-storage": CustomEvent;
    "session-storage": CustomEvent;
  }
}

type SetValue<T> = Dispatch<SetStateAction<T>>;
type StorageType = "local" | "session";

export function useLocalStorage<T>(
  key: string,
  initialValue: T,
  storageType: StorageType = "local"
): [T, SetValue<T>] {
  // Add a Wingspan prefix for to all keys
  const getKey = useCallback(() => {
    return `wingspan:${key}`;
  }, [key]);

  // Get the appropriate storage object
  const getStorage = useCallback(() => {
    return storageType === "local"
      ? window.localStorage
      : window.sessionStorage;
  }, [storageType]);

  const readValue = useCallback((): T => {
    try {
      const storage = getStorage();
      const item = storage.getItem(getKey());
      if (!item) {
        return initialValue;
      }
      const parsed = parseJSON<T>(item);
      // If parseJSON returned undefined but the stored value wasn't "undefined", parsing failed
      if (parsed === undefined && item !== "undefined") {
        return initialValue;
      }
      return parsed as T;
    } catch (error) {
      console.warn(
        `Error reading ${storageType}Storage key "${getKey()}":`,
        error
      );
      return initialValue;
    }
  }, [initialValue, getKey, getStorage, storageType]);

  // Handles storing and setting value for current instance of hook
  const [storedValue, setStoredValue] = useState<T>(readValue);

  // Custom value setter which will write value to storage, local state and will trigger event so other instances will pick it up
  const setValue: SetValue<T> = useCallback(
    value => {
      try {
        // Allow value to be a function so we have the same API as useState
        const newValue = value instanceof Function ? value(storedValue) : value;

        // Save to storage
        const storage = getStorage();
        storage.setItem(getKey(), JSON.stringify(newValue));

        // Save state
        setStoredValue(newValue);

        // We dispatch a custom event so every useLocalStorage hook are notified
        const eventName =
          storageType === "local" ? "local-storage" : "session-storage";
        window.dispatchEvent(new Event(eventName));
      } catch (error) {
        console.warn(
          `Error setting ${storageType}Storage key "${getKey()}":`,
          error
        );
      }
    },
    [getKey, storedValue, getStorage, storageType]
  );

  // Make sure outside changes of value will reflect in current instance
  const handleStorageChange = useCallback(
    (event: StorageEvent | CustomEvent) => {
      const key = getKey();
      if ((event as StorageEvent)?.key && (event as StorageEvent).key !== key) {
        return;
      }
      setStoredValue(readValue());
    },
    [getKey, readValue]
  );
  useEffect(() => {
    const customEventName =
      storageType === "local" ? "local-storage" : "session-storage";

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener(customEventName, handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener(customEventName, handleStorageChange);
    };
  }, [handleStorageChange, storageType]);

  return [storedValue, setValue];
}

// A wrapper for "JSON.parse()"" to support "undefined" value
function parseJSON<T>(value: string | null): T | undefined {
  try {
    return value === "undefined" ? undefined : JSON.parse(value ?? "");
  } catch {
    console.log("parsing error on", { value });
    return undefined;
  }
}
